import { Component, EventEmitter, Injectable, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { HttpStatusCode } from 'axios';
import { SessionStorageService } from '../../../../core/services/session-storage/session-storage.service';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { MaintenanceAttachmentsService } from './maintenance-attachments-service/maintenance-attachments.service';
import { AllDocumentItem, DeleteDocumentRequest } from './maintenance-attachments-interface/maintenance-attachments.interface';
import { Router } from '@angular/router';
import { ConfirmPopupComponent } from '../../../agent-onboarding/new-rep-application/confirm-popup/confirm-popup.component';
import { MatDialog } from '@angular/material/dialog';

/** A supporting-document upload tied to one maintenance section. */
export interface SupportingDocField {
  /** Form control name on this section's form group. */
  control: string;
  label: string;
  icon: string;
}

/**
 * Maintenance-only "Additional Requirements" attachments — the Letter of
 * Undertaking and Securities Interest Register uploads (the shared onboarding
 * attachments component keeps its full set), plus one supporting-document upload
 * per maintenance section.
 */
@Component({
  selector: 'app-maintenance-attachments',
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, MatButtonModule],
  templateUrl: './maintenance-attachments.component.html',
  styleUrl: './maintenance-attachments.component.scss'
})



export class MaintenanceAttachmentsComponent implements OnInit {
  sectionFile: HTMLInputElement | undefined;
  viewattachments: AllDocumentItem[] = [];
  @Output() preview = new EventEmitter<string[]>();
  @Input() formGroup!: FormGroup;
  @Input() readonly = signal(false);
  @Output() save = new EventEmitter<string>();
  private fb = inject(FormBuilder);
  private maintenanceAttachmentsService = inject(MaintenanceAttachmentsService);
  private sessionStorageService = inject(SessionStorageService);
  private snackBarService = inject(SnackbarService);
  private matDialog = inject(MatDialog);
  private router = inject(Router);
  readonly message = 'This field is required.';
  data = history.state;

  isVisible = signal(false);
  showEmptyBox = false;
  /**
   * Only the banking document is collected here. Every other section carries its
   * own supporting-document upload inline, in maintenace-view.
   */
  readonly supportingDocFields: SupportingDocField[] = [
    { control: 'bankDetailsSupportingDocument', label: 'Banking Details', icon: 'account_balance' }
  ];

  ngOnInit(): void {

    const add = (name: string, required: boolean = true) => {

      if (!this.formGroup.get(name)) {
        this.formGroup.addControl(
          name,
          this.fb.control(
            null,
            required ? Validators.required : []
          )
        );
      }
    };

    // Mandatory - 1
    add('letterOfUndertaking');
    add('letterOfUndertakingFile');

    // Mandatory - 2
    add('securitiesInterestRegister');
    add('securitiesInterestRegisterFile');

    // Optional - Section Supporting Documents
    this.supportingDocFields.forEach((field) => {
      add(field.control, false);
      add(field.control + 'File', false);
    });

    add('otherAttachments', false);
    add('otherAttachmentsFile', false);

    this.viewDocuments();

    this.applyReadonlyState();
  }

  /** Count of section supporting documents attached so far. */
  get otherDocs(): AllDocumentItem[] {
    const uploaded = JSON.parse(sessionStorage.getItem('uploadedIds') ?? '[]');
    return this.viewattachments
      .filter(i => i.onboardingDocumentType?.trim().toLowerCase() === 'otherattachments'
        && (this.readonly() || uploaded.includes(String(i.id))))
      .sort((a, b) => Number(a.id) - Number(b.id));
  }

  get attachedCount(): number {
    return this.supportingDocFields.filter((field) => !!this.fileName(field.control)).length;
  }


  onFileSelected(event: any, controlName: string, fileVar: string): void {
    if (this.isVisible()) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (!file) {
      control.setValue(null);
      return;
    }

    const allowedTypes = new Set([
      'application/pdf',
      'image/jpeg',
      'image/png'
    ]);

    const maxSize = 5 * 1024 * 1024; // 5MB

    control.markAsTouched();

    // Validate BEFORE touching the form — a rejected file must never
    // reach patchValue, or fileName() will show its name anyway.
    if (!allowedTypes.has(file.type)) {
      control.setErrors({ invalidType: true });
      input.value = '';
      return;
    }

    if (file.size > maxSize) {
      control.setErrors({ maxSize: true });
      input.value = '';
      return;
    }

    control.setErrors(null);

    // Only now — file passed validation — update the form.
    this.formGroup.patchValue({
      [fileVar]: file,
      [controlName]: file.name
    });

    control.updateValueAndValidity();
    this.uploadDocument(fileVar, controlName);
  }


  isUploading = signal(false);
  uploadingControl: string | null = null;

  async uploadDocument(fileVar: string, controlName: string) {

    this.isVisible.set(true);
    this.isUploading.set(true);
    this.uploadingControl = controlName;

    const categoryMap: Record<string, string> = {
      letterOfUndertaking: 'LetterofUndertakingforRegisterofInterestsinSecurities',
      securitiesInterestRegister: 'Securities Interest Register',
      bankDetailsSupportingDocument: 'Banking Details',
      otherAttachments: 'OtherAttachments'
    };

    const categoryName = categoryMap[controlName];

    const formData = new FormData();

    formData.append('file', this.formGroup.get(fileVar)?.value);
    formData.append('Description', categoryName);
    formData.append('filename', this.formGroup.get(fileVar)?.value?.name);
    formData.append('UserId', String(this.data.applicant.onboardingid));

    const responseData = await this.maintenanceAttachmentsService
      .uploadDocuments(formData)
      .then((response: string) => JSON.parse(response))
      .catch((error: any) => {
        return error;
      });

    if (responseData.status === HttpStatusCode.Ok) {
      this.formGroup.patchValue({
        // [controlName]: responseData.data.responsedata
        [controlName]: this.formGroup.get(fileVar)?.value?.name
      });

      // NEW — this file was uploaded (and saved to backend) in the current session
      this.freshlyUploadedControls.add(controlName);

      await this.viewDocuments();
      //
      const updatedocument = this.getLatestAttachment(categoryName);
      if (updatedocument) sessionStorage.setItem('uploadedIds', JSON.stringify([...JSON.parse(sessionStorage.getItem('uploadedIds') ?? '[]'), String(updatedocument.id)]));

      this.viewattachments = [...this.viewattachments];
      this.showEmptyBox = false;

      this.snackBarService.openSuccessSnackBar('document uploaded successfully!');
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        this.snackBarService.openFailureSnackBar("We couldn't process your request. Please try again");
      }
    }

    this.isVisible.set(false);
    this.isUploading.set(false);
    this.uploadingControl = null;
  }

  private deletingControls = new Set<string>();

  private freshlyUploadedControls = new Set<string>();


  // ids of pre-existing docs removed from UI only (no backend delete API)
  private removedAttachmentIds = new Set<string>();



  async clearFile(controlName: string, input: HTMLInputElement, fileVar: string, doc?: AllDocumentItem): Promise<void> {

    // if (this.isVisible()) {
    //   return;
    // }
    const role = String(this.sessionStorageService.getCurrentUserRole() ?? '').toUpperCase();
    if (this.isVisible() || !role.includes('DSU')) {
      return;
    }

    this.isVisible.set(true);

    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to remove the attachement?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(async (result: any) => {

      // User clicked Cancel / closed the dialog
      if (result?.data !== true) {
        this.isVisible.set(false);
        return;
      }

      if (this.deletingControls.has(controlName)) {
        this.isVisible.set(false);
        return;
      }
      this.deletingControls.add(controlName);

      const control = this.formGroup.get(controlName);
      if (!control) {
        this.deletingControls.delete(controlName);
        this.isVisible.set(false);
        return;
      }

      const categoryMap: Record<string, string> = {
        letterOfUndertaking: 'LetterofUndertakingforRegisterofInterestsinSecurities',
        securitiesInterestRegister: 'Securities Interest Register',
        bankDetailsSupportingDocument: 'Banking Details',
        otherAttachments: 'OtherAttachments'
      };

      const categoryName = categoryMap[controlName];

      // const attachment = this.viewattachments?.find(
      //   (item) =>
      //     item?.onboardingDocumentType?.trim().toLowerCase() === categoryName?.trim().toLowerCase()
      // );
      const attachment = doc ?? this.getLatestAttachment(categoryName);

      if (!attachment) {
        control.setValue(null);
        control.updateValueAndValidity();
        input.value = '';
        this.deletingControls.delete(controlName);
        this.isVisible.set(false);
        return;
      }

      const data: DeleteDocumentRequest = {
        id: attachment.id,
        docTypeId: attachment.docTypeId,
        onboardingId: this.data.applicant.onboardingid
      };

      // if (this.freshlyUploadedControls.has(controlName)) {
      // if ((doc || this.freshlyUploadedControls.has(controlName)) || JSON.parse(sessionStorage.getItem('uploadedIds') ?? '[]').includes(String(attachment.id))) {
      if (JSON.parse(sessionStorage.getItem('uploadedIds') ?? '[]').includes(String(attachment.id))) {

        // Freshly uploaded this session — it's saved on backend, so actually delete it
        const responseData = await this.maintenanceAttachmentsService
          .deleteDocument(data)
          .then((response: string) => JSON.parse(response))
          .catch((error: any) => {
            console.error('DELETE API Error:', error);
            return null;
          });

        if (responseData?.status === HttpStatusCode.Ok) {
          control.setValue(null);
          control.updateValueAndValidity();
          input.value = '';
          this.freshlyUploadedControls.delete(controlName);

          this.viewattachments = this.viewattachments.filter(item => item.id !== attachment.id);

          this.snackBarService.openSuccessSnackBar('Document removed successfully!');
        } else {
          this.snackBarService.openFailureSnackBar("We couldn't process your request. Please try again");
        }

      } else {
        // Pre-existing (NEWREP) document: remove from only ui, backend call doesn't exsist,
        // this.viewattachments
        //   .filter(
        //     (item) =>
        //       item?.onboardingDocumentType?.trim().toLowerCase() === categoryName.trim().toLowerCase()
        //   )
        //   .forEach((item) => this.removedAttachmentIds.add(String(item.id)));
        // this.viewattachments = this.viewattachments.filter(
        //   (item) =>
        //     item?.onboardingDocumentType?.trim().toLowerCase() !== categoryName.trim().toLowerCase()
        // );
        this.removedAttachmentIds.add(String(attachment.id));
        this.viewattachments = this.viewattachments.filter(item => item.id !== attachment.id);

        control.setValue(null);
        control.updateValueAndValidity();
        input.value = '';

        this.snackBarService.openSuccessSnackBar('Document removed successfully!');
      }

      this.deletingControls.delete(controlName);
      this.isVisible.set(false);
    });
  }


  async viewDocuments(): Promise<void> {

    const repCode = this.data.applicant.repcode;


    const response = await this.maintenanceAttachmentsService
      .getAdditionalDocuments(repCode)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        return error;
      });

    // this.viewattachments = response?.data?.responsedata ?? [];
    this.viewattachments = (response?.data?.responsedata ?? []).filter(
      (item: AllDocumentItem) => !this.removedAttachmentIds.has(String(item.id))
    );



    //dupliacte check
    const categoryCounts: Record<string, number> = {};
    this.viewattachments.forEach((item) => {
      const key = item.onboardingDocumentType?.trim().toLowerCase();
      categoryCounts[key] = (categoryCounts[key] || 0) + 1;
    });


    // NEW — existing documents gets patch in form controls
    this.patchExistingDocuments();

    this.applyReadonlyState();
  }

  /** Returns the most recently uploaded attachment for a category — highest id wins if duplicates exist. */
  private getLatestAttachment(categoryName: string): AllDocumentItem | undefined {
    const matches = this.viewattachments.filter(
      (item) =>
        item?.onboardingDocumentType?.trim().toLowerCase() === categoryName.trim().toLowerCase()
    );
    if (!matches.length) return undefined;
    // return matches.reduce((latest, item) => (item.id > latest.id ? item : latest));
    return matches.reduce((latest, item) => (Number(item.id) > Number(latest.id) ? item : latest));
  }

  // Matches each viewattachments item to its form control and patches the value.//
  private patchExistingDocuments(): void {

    const categoryMap: Record<string, string> = {
      letterOfUndertaking: 'LetterofUndertakingforRegisterofInterestsinSecurities',
      securitiesInterestRegister: 'Securities Interest Register',
      bankDetailsSupportingDocument: 'Banking Details',
      otherAttachments: 'OtherAttachments'
    };

    const attachmentByCategory = new Map<string, AllDocumentItem>();

    this.viewattachments.forEach((item) => {
      if (!item?.onboardingDocumentType) return;
      attachmentByCategory.set(
        item.onboardingDocumentType.trim().toLowerCase(),
        item
      );
    });


    Object.entries(categoryMap).forEach(([controlName, categoryName]) => {
      const control = this.formGroup.get(controlName);
      if (!control) return;


      // const attachment = this.getLatestAttachment(categoryName);
      let attachment = this.getLatestAttachment(categoryName);
      const uploaded = JSON.parse(sessionStorage.getItem('uploadedIds') ?? '[]');
      if (controlName === 'bankDetailsSupportingDocument' && attachment && !uploaded.includes(String(attachment.id))) {
        attachment = undefined;
      }


      // const attachment = attachmentByCategory.get(categoryName.trim().toLowerCase());

      // if (attachment) {
      //   control.setValue(attachment.fileName);
      // } else {

      //   control.setValue(null);
      // }
      if (attachment) {
        control.setValue(attachment.fileName);
      } else if (!this.freshlyUploadedControls.has(controlName)) {
        control.setValue(null);
      }

      control.updateValueAndValidity();
    });

  }

  fileName(controlName: string): string {
    const value = this.formGroup.get(controlName)?.value;

    if (!value) return '';

    if (typeof value === 'string') return value;

    if (value instanceof File) return value.name;

    return value.fileName || value.filename || value.name || 'Uploaded';
  }

  // True only when a real, saved attachment exists for this control — not just a form-control value. //
  hasAttachment(controlName: string): boolean {
    if (!this.viewattachments?.length) return false;

    const categoryMap: Record<string, string> = {
      letterOfUndertaking: 'LetterofUndertakingforRegisterofInterestsinSecurities',
      securitiesInterestRegister: 'Securities Interest Register',
      bankDetailsSupportingDocument: 'Banking Details',
      otherAttachments: 'OtherAttachments'
    };

    const categoryName = categoryMap[controlName];
    if (!categoryName) return false;

    // return this.viewattachments.some(
    //   (item) =>
    //     item?.onboardingDocumentType?.trim().toLowerCase() === categoryName.trim().toLowerCase()
    // );
    const uploaded = JSON.parse(sessionStorage.getItem('uploadedIds') ?? '[]');
    return this.viewattachments.some(
      (item) =>
        item?.onboardingDocumentType?.trim().toLowerCase() === categoryName.trim().toLowerCase() &&
        (controlName !== 'bankDetailsSupportingDocument' || uploaded.includes(String(item.id)))
    );
  }

  private applyReadonlyState(): void {
    if (this.readonly() === true) {
      this.formGroup?.disable();
    } else {
      this.formGroup?.enable();
    }
  }

  previewAttachment(controlName: string, doc?: AllDocumentItem): void {
    if (!this.viewattachments) return;

    const categoryMap: Record<string, string> = {
      letterOfUndertaking: 'LetterofUndertakingforRegisterofInterestsinSecurities',
      securitiesInterestRegister: 'Securities Interest Register',
      bankDetailsSupportingDocument: 'Banking Details',
      otherAttachments: 'OtherAttachments'
    };

    const categoryName = categoryMap[controlName];

    const attachment = doc ?? this.getLatestAttachment(categoryName);

    if (!attachment) {
      console.warn('No attachment found for control:', controlName);
      return;
    }

    const filename = doc?.fileName ?? this.formGroup.get(controlName)?.value;
    this.preview.emit([attachment.id, filename]);
  }

}




